import { validate, version } from "uuid";
import { BootgridSettings } from "./bootgridSettings";
import { luscinia } from "../luscinia";
import { BootgridViewType } from "./ViewManager/viewManagerTypes";
// Use the global Moment instance loaded by the page to avoid duplicate Moment builds and locale conflicts
declare const moment: any;

/**
 * Bootgrid formater.
 * 
 * Allows formatting types to be displayed correctly in the bootgrid.
 * @param bootgrid
 * @param bootgridSettings
 * @param config
 * @returns
 */
export function constructBootgridFormatters(bootgrid: any, bootgridSettings: BootgridSettings, config: Config,) {
    const bootgridFormatters = {
        // The edit and delete buttons.
        "commands": function (column: any, row: any[]) {
            var id = row[bootgrid.data(luscinia.data.keyColumnName)];
            if (bootgridSettings.recordView !== "" || bootgridSettings.viewType === BootgridViewType.Grid) {
                let href = `${bootgridSettings.recordView}?action=edit&id=${id}`;
                if (bootgridSettings.isParameterBootgrid) {
                    href = 'javascript:void(0)';
                }
                
                let editBtn = "";
                if (bootgridSettings.isRowEditButtonVisible) {
                    if (bootgridSettings.recordView === "") {
                        editBtn = `<button class='btn btn-outline-success py-0 px-1 command-edit' data-row-id='${id}' ><span class='fas fa-pencil-alt'></span></button>`;
                    } else {
                        editBtn = `<a class='btn btn-outline-success py-0 px-1 command-edit' href='${href}'><span class='fas fa-pencil-alt'></span></a>`;
                    }
                }
                var deleteBtn = bootgridSettings.isRowDeleteButtonVisible ? `<button type='button' data-row-id='${id}' class='btn btn-outline-danger py-0 px-1 command-delete'><span class='fa fa-trash'></span></button>` : "";
                return `<div class='d-flex gap-1'> ${editBtn} ${deleteBtn}</div>`;
            }
            else {
                switch (bootgridSettings.customBtn) {
                    case "UnDelete":
                        return `<button type='button' class='btn btn-outline-success' onclick='luscinia.unDelete('${id}')'><i class='fa fa-undo'></i></button>`;
                    default:
                        return "";
                }
            }
        },
        "Date": function (column: any, row: string[]) {
            var dateTimeOffset = row[column.id];
            if (dateTimeOffset !== null && dateTimeOffset !== undefined && dateTimeOffset !== "") {
                return moment(dateTimeOffset).locale(config.culture).format('L');
            } else {
                return "";
            }
        },
        "DateTime": function (column: any, row: any[]) {
            var dateTimeOffset = row[column.id];
            if (dateTimeOffset !== null) {
                return moment(dateTimeOffset).locale(config.culture).format('L LTS');
            } else {
                return "";
            }
        },
        "Time": function (column: any, row: any[]) {
            if (row[column.id] !== null) {
                return row[column.id];
            } else {
                return "";
            }
        },
        "Checkbox": function (column: any, row: any[]) {
            if (row[column.id]) {
                return "<div class='text-center'><span class='fa fa-check' aria-hidden='true'></span></div>";
            } else {
                return "";
            }
        },
        "InlineCheckbox": function (column: any, row: any[]) {
            var url = bootgrid.data(luscinia.data.modalUrl);
            if (url === undefined || url === "") {
                url = bootgrid.data(luscinia.data.baseUrl);
            }
            return `<div class='d-flex justify-content-center form-check'>
                        <input
                            type="checkbox"
                            class='form-check-input'
                            name="${column.id}" 
                            id="${row[bootgrid.data(luscinia.data.keyColumnName)]}-${String(column.id).toLowerCase()}" 
                            ${row[column.id] ? "checked" : ""}
                            modulepath="${url}"
                            onChange="luscinia.toggleBootgridCheckbox('${url}/togglecheck/${String(column.id).toLowerCase()}/${row[(bootgrid as any).data(luscinia.data.keyColumnName)]}', '${bootgrid.attr('id')}')"
                            data-${luscinia.data.postValueChanged}
                        ></input>
                    </div>`;
        },
        "Password": function (column: any, row: any[]) {
            return "●●●●●●●●";
        },
        "SortOrder": function (column: any, row: any[]) {
            var url = bootgrid.data(luscinia.data.modalUrl);
            if (url === undefined || url === "") {
                url = bootgrid.data(luscinia.data.baseUrl);
            }
            return `
                <div class='text-center'>
                    <button class='btn btn-outline-secondary btn-light' aria-hidden='true' onclick="luscinia.setSortOrder('${url}/sortorder/up/${row[(bootgrid as any).data(luscinia.data.keyColumnName)]}', '${bootgrid.attr('id')}')">
                        <i class='fa fa-arrow-circle-up'></i>
                    </button>
                    <button class='btn btn-outline-secondary btn-light' aria-hidden='true'  onclick="luscinia.setSortOrder('${url}/sortorder/down/${row[bootgrid.data(luscinia.data.keyColumnName)]}', '${bootgrid.attr('id')}')">
                        <i class='fa fa-arrow-circle-down'></i>
                    </button>
                </div>`;
        },
        "Style": function (column: any, row: any[]) {
            return `<div class='text-center'><span class='label label-${row[column.id]}'><i class='fa fa-paint-brush'></i></span></div>`;
        },
        "ImageClass": function (column: any, row: any[]) {
            return `<div class='text-center'><i class='${row[column.id]}'></i></div>`;
        },
        "Image": function (column: any, row: any[]) {
            const value = row[column.id];
            if (value) {
                let imageUrl = isValidCSharpGuid(value) ? `/file/${value}` : `/file/by-path${value.replaceAll("\\", "/")}`;
                return `<div class='text-center'><img width=60 height=60 style="object-fit: contain;" src='${imageUrl}'></i></div>`;
            }
        },
        "Url": function (column: any, row: any[]) {
            return `<a href='${row[column.id]}' target='_blank'>${row[column.id]}</a>`;
        },
        "Email": function (column: any, row: any[]) {
            return `<a href='mailto:${row[column.id]}'>${row[column.id]}</a>`;
        },
        "PhoneNumber": function (column: any, row: any[]) {
            return `<a href='tel:${row[column.id]}'>${row[column.id]}</a>`;
        },
        "File": function (column: any, row: any[]) {
            if (row[column.id] !== null) {
                var url = bootgrid.data(luscinia.data.modalUrl);
                if (url === undefined || url === "") {
                    url = bootgrid.data(luscinia.data.baseUrl);
                }
                return `<a href="${url}/filebyrecord?id=${row[bootgrid.data(luscinia.data.keyColumnName)]}&propertyName=${column.id}" target='_blank'>${row[column.id]}</a>`;
            } else {
                return "";
            }
        },
        "Address": function (column: any, row: any[]) {
            if (row[column.id] !== null && row[column.id] !== "") {
                return "<a href='https://maps.google.com?q=" + row[column.id] + "' target='_blank'><i class='fa fa-map-pin'></i> " + row[column.id] + "</a>";
            } else {
                return "";
            }
        },
        "RecordUrl": function (column: any, row: any[]) {
            var url = bootgrid.data(luscinia.data.modalUrl);
            if (url === undefined || url === "") {
                url = bootgrid.data(luscinia.data.baseUrl);
            }
            return `<a href='${url}/recordurl/${row[bootgrid.data(luscinia.data.keyColumnName)]}' target='_blank'>${row[column.id]}</a>`;
        },
        "Percentage": function (column: any, row: any[]) {
            if (isNaN(row[column.id])) {
                return "";
            } else {
                return `${Number(row[column.id]).toLocaleString(config.culture, { style: 'percent', minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
            }
        },
        "Decimal": function (column: any, row: any[]) {
            if (isNaN(row[column.id])) {
                return "";
            } else {
                return `${Number(row[column.id]).toLocaleString(config.culture)}`;
            }
        },
        "Currency": function (column: any, row: any[]) {
            if (isNaN(row[column.id])) {
                return "";
            } else {
                return `${config.translations.currencySign} ${Number(row[column.id]).toLocaleString(config.culture, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
            }
        },
        "User": function (column: any, row: any[]) {
            return `<div class='text-center'><img width=20 height=20 src='/UserImage/${row[column.id]}'></i> ${row[column.id]}</div>`;
        },
        "InlineEditable": function (column: any, row: any[]) {
            return `<input data-luscinia-postvaluechange data-columnid="${column.id}" type="number" class="inline-editable form-control columnsearchfield" step="0.5" value="${Number(row[column.id])}" name="${column.id}">`;
        },

        "Number": function(column: any, row: any[]) {
            return `${Number(row[column.id])}`;
        },
        "ColorPicker": function (column, row) {
            return `<div class='text-center' style='display: flex; align-items: center; justify-content: center;'>
                <div style='margin-right: 8px; width: 20px; height: 20px; background-color: ${row[column.id]};'></div>
                <span>${row[column.id]}</span>
            </div>`;
        }
    };
    return bootgridFormatters;
}

function isValidCSharpGuid(str: string) {
  return validate(str) && version(str) === 4;
}